import * as React from "react";
import { useState, type InputHTMLAttributes } from "react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/design-system/primitives";
import { Icon } from "@/lib/Icon";
import { cn } from "@/lib/cn";

// Direct-URL signup page for admins / influencers who need a portal
// account without going through the lander purchase flow. NOT linked from
// /login or any other page — distribution is by sharing the URL directly.
//
// Counterpart to LoginScreen.tsx which uses `shouldCreateUser:false` to
// block the "type any email, get a portal account" loophole on the
// public sign-in page. This page intentionally allows user creation
// (`shouldCreateUser:true`) and passes the captured first/last name as
// user_metadata so the backend's bootstrap flow (`_split_name` in
// onboarding_service.py) auto-populates the patient row + Healthie
// chart on first sign-in.
type Mode = "signup" | "magic_link_sent";

export function SignupScreen() {
  const [mode, setMode] = useState<Mode>("signup");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setError("Fill in every field first.");
      return;
    }
    // Single-initial names trigger pharmacy "name not full" clarifications
    // and cancelled fills — require the full name up front.
    if (firstName.trim().length < 2 || lastName.trim().length < 2) {
      setError(
        "Please enter your full first and last name (not just an initial) as they appear on your ID.",
      );
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/app`,
          shouldCreateUser: true,
          data: {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            full_name: `${firstName.trim()} ${lastName.trim()}`,
          },
        },
      });
      if (error) throw error;
      setMode("magic_link_sent");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Signup link send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      {mode === "magic_link_sent" ? (
        <ConfirmationMessage
          title="Check your email"
          body={`We sent a confirmation link to ${email}. Open it on this device to finish creating your account.`}
          onBack={() => setMode("signup")}
        />
      ) : (
        <SignupForm
          firstName={firstName}
          lastName={lastName}
          email={email}
          error={error}
          busy={busy}
          onFirstNameChange={setFirstName}
          onLastNameChange={setLastName}
          onEmailChange={setEmail}
          onSubmit={handleSubmit}
        />
      )}
    </Shell>
  );
}

/* ---------- Layout shell (duplicated from LoginScreen to keep the two
 * pages independent — they'll likely diverge as we evolve them) ---------- */

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col bg-white text-black">
      <TopBar />
      <main className="flex-1 flex flex-col px-15 md:px-30">
        <div className="w-full max-w-[44rem] mx-auto flex-1 flex flex-col justify-center pt-60 pb-60 md:pt-100 md:pb-100">
          {children}
        </div>
      </main>
    </div>
  );
}

function TopBar() {
  return (
    <header className="flex items-center justify-between border-b border-black/10 bg-[#ECECEC]">
      <div className="w-[5rem] h-[5rem] flex items-center justify-center bg-slate">
        <div className="w-full max-w-[3rem] text-black">
          <Icon name="Mark Line" viewBox="0 0 32 30" />
        </div>
      </div>
      <div className="font-mono text-12 uppercase text-black px-15">
        Patient Portal
      </div>
    </header>
  );
}

/* ---------- Form states ---------- */

interface SignupFormProps {
  firstName: string;
  lastName: string;
  email: string;
  error: string | null;
  busy: boolean;
  onFirstNameChange: (v: string) => void;
  onLastNameChange: (v: string) => void;
  onEmailChange: (v: string) => void;
  onSubmit: (e: React.SyntheticEvent<HTMLFormElement>) => void;
}

function SignupForm({
  firstName,
  lastName,
  email,
  error,
  busy,
  onFirstNameChange,
  onLastNameChange,
  onEmailChange,
  onSubmit,
}: SignupFormProps) {
  return (
    <div className="flex flex-col gap-25">
      <h1 className="type-headline-lg text-black">Create your account</h1>
      <p className="font-body text-15 text-[#7C7C7C]">
        Enter your name and email. We&apos;ll send you a one-tap link to
        confirm and sign in.
      </p>

      <form onSubmit={onSubmit} className="flex flex-col gap-15">
        <div className="flex flex-col md:flex-row gap-15">
          <Field
            type="text"
            placeholder="First name"
            autoComplete="given-name"
            required
            value={firstName}
            onChange={(e) => onFirstNameChange(e.target.value)}
          />
          <Field
            type="text"
            placeholder="Last name"
            autoComplete="family-name"
            required
            value={lastName}
            onChange={(e) => onLastNameChange(e.target.value)}
          />
        </div>
        <Field
          type="email"
          placeholder="Email address"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
        />

        {error && (
          <div role="alert" className="font-body text-13 text-danger">
            {error}
          </div>
        )}

        <Button type="submit" variant="filled" loading={busy} className="mt-5">
          <span>Email me a confirmation link</span>
          <span className="flex items-center justify-center w-8">
            <Icon name="Arrow" viewBox="0 0 10 12" className="w-8" />
          </span>
        </Button>
      </form>
    </div>
  );
}

function ConfirmationMessage({
  title,
  body,
  onBack,
}: {
  title: string;
  body: string;
  onBack: () => void;
}) {
  return (
    <div className="flex flex-col gap-25">
      <h1 className="type-headline-lg text-black">{title}</h1>
      <div className="font-body text-15 text-[#7C7C7C]">{body}</div>
      <div className="mt-15 flex items-center justify-start gap-15">
        <button type="button" onClick={onBack} className="footer-link">
          Back
        </button>
      </div>
    </div>
  );
}

/* ---------- Form primitives ---------- */

function Field({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={cn(
        "bg-[#FAFAFA] border border-[#ECECEC] text-[#7C7C7C] placeholder:text-[#7C7C7C]",
        "rounded-none outline-none py-12 px-15 w-full min-w-0",
        "focus:outline-none",
        "font-body text-15",
        className,
      )}
    />
  );
}
